import type { SpaceId } from "@common/types/space";
import { CLIENT_DEFAULT_SPACE_ID } from "@common/types/space";
import type { GroupKey, Todo, TodoId } from "@common/types/todo";
import { Timer } from "@common/use-cases/timer";
import type { TimerStoreSyncPayload } from "@common/use-cases/timer/timer-network-sync";
import type { TimerSyncState } from "@common/use-cases/timer/timer-sync";
import { getTimerSyncState } from "@common/use-cases/timer/timer-sync";
import type { DisplayMode } from "@common/utils/todo-utils";
import {
  autorun,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from "mobx";

export type TimerStoreState =
  | "IDLE_NO_DURATION"
  | "IDLE_NOT_STARTED"
  | "IDLE_COMPLETED"
  | "IN_PROGRESS"
  | "PAUSED"
  | "STOPPED";

export type TimerType =
  | "break" // break in between tasks
  | "todo"; // active task

export type TimerMode =
  | "auto" // when a task finishes, mark as complete and jump to next
  | "manual"; // when a task finishes, wait in completed state

export interface MakeTimerStoreDeps {
  todos: {
    getTodoById: (id: TodoId, spaceId: SpaceId) => Todo | null;
    getNextTodo: (
      id: TodoId,
      spaceId: SpaceId,
      /**
       * While syncing remote state, groupKey may not be consistent across devices
       */
      groupKey: GroupKey | null
    ) => Todo | null;
    updateTodo: (id: TodoId, spaceId: SpaceId, value: Partial<Todo>) => void;
    setTodoComplete: (
      id: TodoId,
      spaceId: SpaceId,
      complete: boolean,
      moveToTodayOnComplete: boolean
    ) => void;
  };
  notify: {
    todoComplete: (todo: Todo) => void;
    breakStart: (breakDuration: number) => void;
    breakOver: (todo: Todo | null) => void;
    timerStart: () => void;
  };
  localStorage: {
    setItem: (key: string, value: string) => Promise<void>;
    getItem: (key: string) => Promise<string | null>;
    removeItem: (key: string) => Promise<void>;
  };
  getDisplayMode: () => DisplayMode;
}
export default class TimerStore {
  @observable
  currentTimerType: TimerType = "todo";
  @observable
  timers: Partial<Record<TimerType, Timer>> = {};
  currentTodoId: TodoId | null = null;
  currentTodoGroupKey: GroupKey | null = null;
  currentSpaceId: SpaceId | null = null;
  @observable
  lastBreakCheckpoint: number = 0; // what was timePast() at the point last break was taken
  deps: MakeTimerStoreDeps;

  // preferences
  preferences = {
    workDuration: 25 * 60 * 1000,
    breakDuration: 5 * 60 * 1000,
  };

  @computed
  get _clock() {
    return this.timers[this.currentTimerType]?._clock;
  }

  @computed
  get nextBreakTime() {
    // the concept of pomodoro only works if there is an ongoing timer
    if (!this.timers.todo) return null;
    const totalDuration = this.timers.todo.totalDuration;
    const pomodoroInterval = this.preferences.workDuration;
    const nextBreakTime =
      totalDuration - this.lastBreakCheckpoint - pomodoroInterval;
    return nextBreakTime > 0 && nextBreakTime < totalDuration
      ? nextBreakTime
      : null;
  }

  constructor(deps: MakeTimerStoreDeps) {
    this.deps = deps;
    makeAutoObservable(this);

    const loadPreferences = async () => {
      this.preferences.workDuration =
        Number(await this.deps.localStorage.getItem("TIMER_WORK_DURATION")) ||
        this.preferences.workDuration;
      this.preferences.breakDuration =
        Number(await this.deps.localStorage.getItem("TIMER_BREAK_DURATION")) ||
        this.preferences.breakDuration;
    };

    loadPreferences();

    // when todo completes or is no longer valid, stop timer if it is running
    autorun(() => {
      // if todo completed
      if (!this.currentTodo || !this.currentSpaceId) {
        this.stop();
        return;
      }

      const isComplete = this.currentTodo.completed;
      const isDeleted = !this.currentTodo;
      if (isComplete || isDeleted) {
        const nextTodo = this.deps.todos.getNextTodo(
          this.currentTodo.id,
          this.currentSpaceId,
          this.currentTodoGroupKey
        );
        if (nextTodo && this.currentSpaceId) {
          this.setCurrentTodo(
            nextTodo.id,
            this.currentSpaceId,
            this.currentTodoGroupKey
          );
          return;
        }
        this.stop();
      }
    });
  }

  @computed
  get currentTimer() {
    return this.timers[this.currentTimerType];
  }

  @computed
  get currentTodo() {
    if (!this.currentTodoId) return null;
    if (!this.currentSpaceId) return null;
    return this.deps.todos.getTodoById(this.currentTodoId, this.currentSpaceId);
  }

  @computed
  get isVisible() {
    return !!this.currentTodo && !!this.currentTodoGroupKey;
  }

  @computed
  get isTimerActive() {
    return (
      this.currentTodo &&
      this.currentTimer &&
      this.currentTimer.timerState !== "STOPPED"
    );
  }

  @computed
  getState(): TimerStoreState {
    const currentTodo = this.currentTodo;
    const currentTimer = this.currentTimer;
    if (!currentTodo) return "STOPPED";
    if (!currentTodo.duration) return "IDLE_NO_DURATION";
    if (currentTimer?.timerState === "FINISHED") return "IDLE_COMPLETED";
    if (currentTimer?.timerState === "RUNNING") return "IN_PROGRESS";
    if (currentTimer?.timerState === "PAUSED") return "PAUSED";
    return "IDLE_NOT_STARTED";
  }

  setCurrentTodo(todoId: TodoId, spaceId: SpaceId, groupKey: GroupKey | null) {
    this.cleanupTimer("todo");
    this.cleanupTimer("break");

    const todo = this.deps.todos.getTodoById(todoId, spaceId);
    if (!todo) throw new Error("Todo not found");

    runInAction(() => {
      this.currentTodoId = todoId;
      this.currentSpaceId = spaceId;
      this.currentTodoGroupKey = groupKey;
    });
  }

  startTodoTimer() {
    if (!this.currentTodoId) throw new Error("No todo selected");
    if (!this.currentSpaceId) throw new Error("No space selected");
    const todo = this.deps.todos.getTodoById(
      this.currentTodoId,
      this.currentSpaceId
    );
    if (!todo) throw new Error("Todo not found");
    if (!todo.duration) return;

    this.lastBreakCheckpoint = 0;

    this.deps.notify.timerStart();

    this.newTimer("todo", todo.duration * 1000, (timer) => {
      // leave it in "FINISHED" state

      if (this.currentTimerType === "todo") {
        // stay in idle completed state
      }

      this.deps.notify.todoComplete(todo);
    });
  }

  startBreakTimer(duration = this.preferences.breakDuration) {
    if (this.timers.todo) {
      this.timers.todo.pause();
    }

    this.lastBreakCheckpoint = this.timers.todo?.getTimePast() ?? 0;

    this.newTimer("break", duration, (timer) => {
      timer.stop();

      // finish break
      this.currentTimerType = "todo";
      this.timers.todo?.unpause();

      this.deps.notify.breakOver(this.currentTodo);
    });

    this.deps.notify.breakStart(duration);
  }

  endBreak() {
    if (this.currentTimerType !== "break") throw new Error("Not in break");
    this.timers.break?.finish();
  }

  pause() {
    if (!this.currentTimer) throw new Error("No active timer");
    if (this.currentTimer.timerState !== "RUNNING")
      throw new Error("Timer is not running, can't be paused");
    this.currentTimer.pause();
  }

  unpause() {
    if (!this.currentTimer) throw new Error("No active timer");
    if (this.currentTimer.timerState !== "PAUSED")
      throw new Error("Timer is not paused, can't be unpaused");
    this.currentTimer.unpause();
  }

  extend(duration: number) {
    // init if idle
    if (
      this.currentTodo &&
      this.currentSpaceId &&
      this.getState() === "IDLE_NOT_STARTED"
    ) {
      this.deps.todos.updateTodo(this.currentTodo.id, this.currentSpaceId, {
        duration,
      });
      this.startTodoTimer();
      return;
    }

    const activeTimer = this.timers[this.currentTimerType];
    if (!activeTimer) throw new Error("No active timer");
    if (activeTimer.timerState === "STOPPED") throw new Error("Timer stopped");
    activeTimer.extend(duration);

    if (
      this.currentTimerType === "todo" &&
      this.currentTodo &&
      this.currentSpaceId
    ) {
      this.deps.todos.updateTodo(this.currentTodo.id, this.currentSpaceId, {
        duration: (this.currentTodo.duration ?? 0) + duration / 1000,
      });
    }

    if (
      this.currentTimerType === "todo" &&
      this.timers.todo?.timerState === "FINISHED"
    ) {
      activeTimer.unpause();
    }
  }

  stop() {
    runInAction(() => {
      const activeTimer = this.timers[this.currentTimerType];
      activeTimer?.stop();
      this.cleanupTimer("todo");
      this.cleanupTimer("break");

      this.currentTodoId = null;
      this.currentTodoGroupKey = null;
    });
  }

  fastForward() {
    this.currentTimer?.finish();
  }

  next() {
    if (
      !this.currentTodoId ||
      !this.currentSpaceId ||
      !this.currentTodoGroupKey
    ) {
      this.stop();
      return;
    }

    const next = this.deps.todos.getNextTodo(
      this.currentTodoId,
      this.currentSpaceId,
      this.currentTodoGroupKey
    );
    if (next && this.currentSpaceId) {
      this.setCurrentTodo(
        next.id,
        this.currentSpaceId,
        this.currentTodoGroupKey
      );
      return;
    }

    this.stop();
  }

  public toggleWorkDuration() {
    const workOptions = [25, 30, 35, 40, 45, 50, 55, 60];
    const currentIndex = workOptions.indexOf(
      this.preferences.workDuration / 60 / 1000
    );
    const nextIndex = (currentIndex + 1) % workOptions.length;
    this.preferences.workDuration = workOptions[nextIndex]! * 60 * 1000;

    this.deps.localStorage.setItem(
      "TIMER_WORK_DURATION",
      this.preferences.workDuration.toString()
    );
  }

  public toggleBreakDuration() {
    const breakOptions = [5, 10, 15, 20, 25, 30];
    const currentIndex = breakOptions.indexOf(
      this.preferences.breakDuration / 60 / 1000
    );
    const nextIndex = (currentIndex + 1) % breakOptions.length;
    this.preferences.breakDuration = breakOptions[nextIndex]! * 60 * 1000;

    this.deps.localStorage.setItem(
      "TIMER_BREAK_DURATION",
      this.preferences.breakDuration.toString()
    );
  }

  public completeTodo() {
    if (!this.currentTodo || !this.currentSpaceId) {
      this.stop();
      return;
    }

    this.deps.todos.setTodoComplete(
      this.currentTodo.id,
      this.currentSpaceId,
      true,
      this.deps.getDisplayMode() !== "calendar"
    );
  }

  private newTimer(
    type: TimerType,
    duration: number,
    onFinish: (timer: Timer) => void
  ) {
    this.currentTimerType = type;
    const timer = new Timer();

    this.timers[type] = timer;
    timer.start(duration);

    timer.events.on("ON_TIME_UPDATED", this.onTimerTick.bind(this));
    timer.events.on("ON_TIMER_COMPLETED", onFinish);

    return timer;
  }

  private onTimerTick() {
    // check if need to trigger break time
    if (
      this.currentTimerType === "todo" &&
      this.currentTimer &&
      this.nextBreakTime &&
      this.currentTimer.getTimeLeft() <= this.nextBreakTime
    ) {
      // start pomodoro break
      this.startBreakTimer();
    }
  }

  private cleanupTimer(type: TimerType) {
    runInAction(() => {
      this.timers[type]?.dispose();
      delete this.timers[type];
    });
  }

  // SYNCHRONISING

  /**
   * Sync a store to given state
   */
  syncToState(syncPayload: TimerStoreSyncPayload) {
    runInAction(() => {
      this.currentTimerType = syncPayload.currentTimerType;
      this.currentTodoId = syncPayload.currentTodoId;
      this.currentSpaceId = syncPayload.currentSpaceId;
      this.lastBreakCheckpoint = syncPayload.lastBreakCheckpoint;
      this.preferences = syncPayload.preferences;

      for (const entry of Object.entries(syncPayload.timers)) {
        const [key, value] = entry as [
          keyof typeof this.timers,
          TimerSyncState,
        ];
        const timer = new Timer();
        timer.syncToState(value);
        this.timers[key] = timer;
      }
    });
  }
}
